import { useMutation, useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import {
  AddCreditRequestDto,
  CreditDetailsDto,
  CreditTransactionDto,
  CreditTransactionRequestDto,
  PaginationRequest,
  ResponseDtoCreditTransactionMetadataDto,
  ResponseDtoPageCreditTransactionDto,
} from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';

// Interface for credit transaction filters
export interface CreditTransactionFilters {
  transactionTypes?: string[];
  billedEntities?: string[];
  invoiceNumbers?: string[];
  shipmentCounts?: string[];
  serviceProviders?: string[];
  modalities?: string[];
  poNumbers?: string[];
  creditBilledEntityId?: number;
  dates?: string[];
  page?: number;
  size?: number;
  sortBy?: string;
  sortDirection?: 'ASC' | 'DESC';
}

// Interface for pagination and search parameters
interface CreditTransactionListParams {
  page?: number;
  size?: number;
  sortingColum?: string;
  sortingDirection?: 'ASC' | 'DESC';
}

// Common query keys
const QUERY_KEYS = {
  creditTransactionsList: 'creditTransactionsList',
  creditTransactionsMetadata: 'creditTransactionsMetadata',
  creditDetails: 'creditDetails',
  creditTransactions: 'creditTransactions',
} as const;

// Common query options
const DEFAULT_QUERY_OPTIONS = {
  keepPreviousData: true,
  staleTime: 5 * 60 * 1000, // 5 minutes
  refetchOnWindowFocus: false,
} as const;

// Utility functions to reduce duplication
const buildPaginationRequest = (params: CreditTransactionListParams | CreditTransactionFilters): PaginationRequest => {
  const paginationRequest: any = {};

  if ('page' in params && params.page !== undefined) paginationRequest.page = params.page;
  if ('size' in params && params.size !== undefined) paginationRequest.size = params.size;

  // Handle different property names for sorting
  const sortingColumn = ('sortingColum' in params && params.sortingColum) || ('sortBy' in params && params.sortBy);
  const sortingDirection = ('sortingDirection' in params && params.sortingDirection) || ('sortDirection' in params && params.sortDirection);

  if (sortingColumn) paginationRequest.sortingColum = sortingColumn;
  if (sortingDirection) paginationRequest.sortingDirection = sortingDirection;

  return paginationRequest;
};

const applyFiltersToRequest = (requestDto: CreditTransactionRequestDto, filters: CreditTransactionFilters): void => {
  // Filter mapping to reduce repetitive if statements
  const filterMappings = [
    { condition: filters.transactionTypes?.length, target: 'transactionTypes', value: filters.transactionTypes, transform: (val: string[]) => val as any[] },
    { condition: filters.billedEntities?.length, target: 'billedEntities', value: filters.billedEntities, transform: (val: string[]) => val },
    { condition: filters.invoiceNumbers?.length, target: 'invoiceNumbers', value: filters.invoiceNumbers, transform: (val: string[]) => val },
    { condition: filters.shipmentCounts?.length, target: 'shipmentCounts', value: filters.shipmentCounts, transform: (val: string[]) => val },
    { condition: filters.serviceProviders?.length, target: 'serviceProviders', value: filters.serviceProviders, transform: (val: string[]) => val },
    { condition: filters.modalities?.length, target: 'modalities', value: filters.modalities, transform: (val: string[]) => val as any[] },
    { condition: filters.poNumbers?.length, target: 'poNumbers', value: filters.poNumbers, transform: (val: string[]) => val },
    { condition: filters.dates?.length, target: 'dates', value: filters.dates, transform: (val: string[]) => val },
  ] as const;

  filterMappings.forEach(({ condition, target, value, transform }) => {
    if (condition && value) {
      (requestDto as any)[target] = transform(value);
    }
  });

  // Handle creditBilledEntityId separately since it's a number, not an array
  if (filters.creditBilledEntityId !== undefined) {
    requestDto.creditBillableEntityId = filters.creditBilledEntityId;
  }
};

const buildCreditTransactionRequest = (params: CreditTransactionListParams, filters: CreditTransactionFilters = {}): CreditTransactionRequestDto => {
  const requestDto: CreditTransactionRequestDto = {
    paginationRequest: buildPaginationRequest(params),
  };

  applyFiltersToRequest(requestDto, filters);
  return requestDto;
};

const buildMetadataRequest = (filters: CreditTransactionFilters): CreditTransactionRequestDto => {
  const requestDto: CreditTransactionRequestDto = {
    paginationRequest: buildPaginationRequest(filters),
  };

  applyFiltersToRequest(requestDto, filters);
  return requestDto;
};

// Invalidation utility
const invalidateAllCreditQueries = (queryClient: ReturnType<typeof useQueryClient>) => {
  const queryKeys = Object.values(QUERY_KEYS);
  queryKeys.forEach((key) => queryClient.invalidateQueries([key]));
};

/**
 * Hook to fetch a paginated list of credit transactions using /query endpoint
 * @param params Pagination and sorting parameters
 * @param filters Filter parameters
 * @returns Query result with paginated credit transactions data
 */
export const useCreditTransactionList = (
  params: CreditTransactionListParams,
  filters?: CreditTransactionFilters
): UseQueryResult<ResponseDtoPageCreditTransactionDto> =>
  useQuery(
    [QUERY_KEYS.creditTransactionsList, params, filters],
    async () => {
      const requestDto = buildCreditTransactionRequest(params, filters);
      const response = await tFinancialService.api.getAllCreditTransactions(requestDto);
      return response.data;
    },
    { keepPreviousData: DEFAULT_QUERY_OPTIONS.keepPreviousData }
  );

/**
 * Hook to fetch credit transactions metadata for filtering options
 * This provides filter options for transaction types, billed entities, etc.
 */
export const useCreditTransactionsMetadata = (filters: CreditTransactionFilters = {}): UseQueryResult<ResponseDtoCreditTransactionMetadataDto> =>
  useQuery(
    [QUERY_KEYS.creditTransactionsMetadata, filters],
    async (): Promise<ResponseDtoCreditTransactionMetadataDto> => {
      const requestDto = buildMetadataRequest(filters);
      return (await tFinancialService.api.getAllCreditTransactionsMetaData(requestDto)).data;
    },
    {
      staleTime: DEFAULT_QUERY_OPTIONS.staleTime,
      refetchOnWindowFocus: DEFAULT_QUERY_OPTIONS.refetchOnWindowFocus,
    }
  );

/**
 * Hook to fetch credit details for a billed entity
 * @param billedEntityId The ID of the billed entity
 * @param fromDate The start date for calculating consumed credits
 * @param transactionDates The transaction dates for filtering
 * @returns Query result with credit details data
 */
export const useCreditDetails = (billedEntityId: number, fromDate?: string, transactionDates?: string[]): UseQueryResult<CreditDetailsDto> =>
  useQuery(
    [QUERY_KEYS.creditDetails, { billedEntityId, fromDate, transactionDates }],
    async (): Promise<CreditDetailsDto> => (await tFinancialService.api.getCreditDetails({ billedEntityId, fromDate, transactionDates })).data.data,
    { enabled: !!billedEntityId }
  );

/**
 * Hook to fetch credit transactions for a billed entity
 * @param billedEntityId The ID of the billed entity
 * @returns Query result with credit transactions data
 */
export const useCreditTransactions = (billedEntityId: number): UseQueryResult<CreditTransactionDto[]> =>
  useQuery(
    [QUERY_KEYS.creditTransactions, { billedEntityId }],
    async (): Promise<CreditTransactionDto[]> => (await tFinancialService.api.getCreditTransactions({ billedEntityId })).data.data,
    { enabled: !!billedEntityId }
  );

/**
 * Hook to add credits to a billed entity
 * @returns Mutation function to add credits
 */
export const useAddCredit = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: AddCreditRequestDto) => {
      const response = await tFinancialService.api.addCredit(data);
      return response.data;
    },
    {
      onSuccess: () => {
        invalidateAllCreditQueries(queryClient);
      },
    }
  );
};
